"use client";

import * as React from "react";
import { CircleQuestionMark as HelpCircle, ArrowRight, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How does the Handy Heroes appliance repair match work?",
  answer: "Handy Heroes connects Atlanta homeowners with independent local appliance repair contractors. You submit your appliance issue and location, and we connect you with an available technician in your area."
}, {
  question: "How long does a typical appliance diagnosis take?",
  answer: "Most in-home diagnostic inspections take 30 to 60 minutes. The technician tests electrical components, verifies mechanical movement, and determines which parts require repair or replacement."
}, {
  question: "How is pricing determined for appliance repairs?",
  answer: "Costs vary by appliance type, part required, and technician. After inspecting your appliance, the independent pro provides their own written estimate detailing parts and labor before performing any repair work."
}, {
  question: "Which appliance brands can local pros service?",
  answer: "Homeowners can connect with local pros who repair major brands including Whirlpool, Samsung, LG, GE Appliances, Maytag, Bosch, KitchenAid, Frigidaire, Kenmore, and Sub-Zero."
}, {
  question: "Should I repair or replace my broken appliance?",
  answer: "A common guideline is to compare the repair estimate against the price and lifespan of a replacement. If an older appliance requires a repair exceeding half its replacement cost, the technician can explain options so you make an informed choice."
}, {
  question: "Do pros carry common replacement parts in their vehicles?",
  answer: "Many independent contractors carry frequently needed parts such as thermal fuses, water inlet valves, belts, and relays. For specialized control boards or manufacturer-specific parts, the technician will order the component and schedule a follow-up installation."
}, {
  question: "How quickly can I schedule an appliance repair visit?",
  answer: "Appointment availability varies by contractor schedule and location. When submitting your request, you can select your preferred day and time window, and the matched pro will confirm arrival details."
}];
export default function ApplianceRepairFAQ() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="appliancerepairfaq" className="bg-background py-16 md:py-24 lg:py-32" ref={ref}>
      <div className={`container mx-auto px-4 md:px-6 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
          {}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div>
              <Badge variant="secondary" className="mb-4">
                <Wrench className="mr-1.5 size-3.5" />
                Support & FAQ
              </Badge>
              <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
                Frequently Asked Questions
              </h2>
              <p className="mt-4 text-lg text-muted-foreground max-w-[480px]">
                Find clear answers about how the Handy Heroes marketplace works, technician estimates, scheduling, and appliance service coverage.
              </p>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ContactPage">
                  Contact Support
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="#bookingrequest">
                  Request a Match
                </Link>
              </Button>
            </div>

            <div className="mt-8 rounded-xl bg-card p-6 border border-border shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <HelpCircle className="size-6" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-semibold text-card-foreground">
                    Have more questions?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Our team is here to assist with questions about our marketplace and contractor network.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="w-full">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="rounded-lg border border-border bg-card px-6 shadow-sm transition-colors hover:bg-accent/50 data-[state=open]:bg-accent/50">
                  <AccordionTrigger className="font-default text-left text-base font-semibold text-card-foreground hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}